import { loadFirstRunState, onFirstRunChanged } from "@/shared/onboarding/first-run";
import { hasStoredPreferences, loadPreferences } from "@/shared/preferences/store";
import {
  ANALYTICS_CONSENT_CHOICES,
  analyticsCountryFromLocaleTag,
  normalizeAnalyticsConsentChoice,
  resolveAnalyticsConsent,
  resolveAnalyticsMarketContext,
  type AnalyticsConsentChoice,
  type AnalyticsConsentDecision,
  type AnalyticsMarketContext,
} from "./analytics-consent-policy.mjs";
import { isSignedIn } from "@/shared/auth/session";

// Version 2 deliberately invalidates the older generic analytics choice after
// the disclosure was expanded to cover network-derived approximate geography.
export const ANALYTICS_CONSENT_STORAGE_KEY = "privacy_analytics_consent_choice_v2";
export const ANALYTICS_CLIENT_ID_STORAGE_KEY = "tb_public_web_analytics_client_v1";
export const ANALYTICS_NOTICE_ACK_STORAGE_KEY = "privacy_analytics_notice_ack_v1";

const ANALYTICS_CONSENT_EVENT = "tb-public-web-analytics-consent-changed";
const PREFERENCES_EVENT = "tb-public-web-preferences-changed";
const AUTH_EVENT = "tb-auth-changed";
const AUTH_STORAGE_KEY = "tb_auth_session_v1";
const ACCOUNT_SCOPED_PREFERENCES_KEY_PREFIX = "tb_web_prefs_v1::";

type StoredConsent = {
  choice: AnalyticsConsentChoice;
  loaded: boolean;
};

function readStoredConsent(): StoredConsent {
  if (typeof window === "undefined") {
    return { choice: ANALYTICS_CONSENT_CHOICES.UNKNOWN, loaded: false };
  }
  try {
    return {
      choice: normalizeAnalyticsConsentChoice(
        window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY),
      ),
      loaded: true,
    };
  } catch {
    // If browser storage is unavailable, fail closed: an earlier explicit
    // denial cannot be ruled out.
    return { choice: ANALYTICS_CONSENT_CHOICES.UNKNOWN, loaded: false };
  }
}

function currentAnalyticsNoticeAcknowledged(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(ANALYTICS_NOTICE_ACK_STORAGE_KEY) === "acknowledged";
  } catch {
    return false;
  }
}

export function publicWebDeviceLocaleCountry(): string {
  if (typeof navigator === "undefined") return "";
  const localeTags = [navigator.language, ...(navigator.languages || [])];
  for (const localeTag of localeTags) {
    const country = analyticsCountryFromLocaleTag(localeTag);
    if (country) return country;
  }
  return "";
}

export function publicWebAnalyticsNoticeDefaultEnabled(
  marketCountry: string,
): boolean {
  return resolveAnalyticsConsent({
    marketCountry,
    deviceLocaleCountry: publicWebDeviceLocaleCountry(),
    choice: ANALYTICS_CONSENT_CHOICES.UNKNOWN,
    preferencesLoaded: true,
    marketResolved: true,
    noticeAcknowledged: true,
    globalPrivacyControl: globalPrivacyControlEnabled(),
  }).analyticsStorageGranted;
}

function marketCountryContext(): AnalyticsMarketContext {
  try {
    const firstRun = loadFirstRunState();
    const storedPreferences = hasStoredPreferences();
    return resolveAnalyticsMarketContext({
      authenticatedAccount: isSignedIn(),
      firstRunCompleted: firstRun.completed,
      firstRunCountry: firstRun.country,
      hasStoredPreferences: storedPreferences,
      preferencesCountry: storedPreferences ? loadPreferences().country : undefined,
    });
  } catch {
    return { country: "", resolved: false };
  }
}

function globalPrivacyControlEnabled(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    (
      navigator as Navigator & {
        globalPrivacyControl?: boolean;
      }
    ).globalPrivacyControl === true
  );
}

export function getPublicWebAnalyticsConsent(): AnalyticsConsentDecision {
  const stored = readStoredConsent();
  const market = marketCountryContext();
  return resolveAnalyticsConsent({
    marketCountry: market.country,
    deviceLocaleCountry: publicWebDeviceLocaleCountry(),
    choice: stored.choice,
    preferencesLoaded: stored.loaded,
    marketResolved: market.resolved,
    noticeAcknowledged: currentAnalyticsNoticeAcknowledged(),
    globalPrivacyControl: globalPrivacyControlEnabled(),
  });
}

export function isPublicWebAnalyticsAllowed(): boolean {
  return getPublicWebAnalyticsConsent().analyticsStorageGranted;
}

function clearPersistedAnalyticsIdentity(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ANALYTICS_CLIENT_ID_STORAGE_KEY);
  } catch {
    // The consent gate remains closed even when storage cleanup is blocked.
  }
}

export function setPublicWebAnalyticsEnabled(enabled: boolean): boolean {
  if (typeof window === "undefined") return false;
  const choice = enabled ? ANALYTICS_CONSENT_CHOICES.GRANTED : ANALYTICS_CONSENT_CHOICES.DENIED;
  try {
    window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, choice);
    if (!enabled) clearPersistedAnalyticsIdentity();
    window.dispatchEvent(new CustomEvent(ANALYTICS_CONSENT_EVENT, { detail: { choice } }));
    return true;
  } catch {
    return false;
  }
}

export function acknowledgePublicWebAnalyticsNotice(): boolean {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem(ANALYTICS_NOTICE_ACK_STORAGE_KEY, "acknowledged");
    window.dispatchEvent(new CustomEvent(ANALYTICS_CONSENT_EVENT));
    return true;
  } catch {
    return false;
  }
}

export function onPublicWebAnalyticsConsentChanged(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const unsubscribeFirstRun = onFirstRunChanged(listener);

  const onStorage = (event: StorageEvent) => {
    if (
      event.key === ANALYTICS_CONSENT_STORAGE_KEY ||
      event.key === ANALYTICS_NOTICE_ACK_STORAGE_KEY ||
      event.key === AUTH_STORAGE_KEY ||
      event.key?.startsWith(ACCOUNT_SCOPED_PREFERENCES_KEY_PREFIX)
    ) {
      listener();
    }
  };
  window.addEventListener(ANALYTICS_CONSENT_EVENT, listener);
  window.addEventListener(PREFERENCES_EVENT, listener);
  window.addEventListener(AUTH_EVENT, listener);
  window.addEventListener("storage", onStorage);
  return () => {
    unsubscribeFirstRun();
    window.removeEventListener(ANALYTICS_CONSENT_EVENT, listener);
    window.removeEventListener(PREFERENCES_EVENT, listener);
    window.removeEventListener(AUTH_EVENT, listener);
    window.removeEventListener("storage", onStorage);
  };
}
